import React, { useEffect, useRef } from 'react';
import {
  Code, Terminal, Database, Layout, Server,
  Cpu, Globe, Zap, Briefcase, Users,
  Target, Lightbulb, Compass, MapPin, Award, Cloud, User
} from 'lucide-react';

export default function OrbitSystem({ 
  candidateName, 
  innerIcons = [], 
  outerIcons = [],
  className = ""
}) {
  // Animation Refs
  const innerRingRef = useRef(null);
  const outerRingRef = useRef(null);
  const requestRef = useRef();
  const startTimeRef = useRef();
  
  // Real width scaling
  const [scale, setScale] = React.useState(1);

  // Measure and scale exactly as the user requested
  useEffect(() => {
    const handleResize = () => {
      // Cap at 1 to prevent getting massive on desktop screens.
      // The ring is 560px, but the badges extend outwards by about 100-150px on each side.
      // Let's divide by 720 to get a slightly larger, tighter fit.
      const vw = window.innerWidth;
      setScale(Math.min(1, vw / 720));
    };
    
    // Set initial scale and listen
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation Loop for perfect sync
  useEffect(() => {
    const animate = (time) => {
      if (!startTimeRef.current) startTimeRef.current = time;
      const elapsed = time - startTimeRef.current;

      // Inner: Counter-Clockwise (negative angle)
      // Outer: Clockwise (positive angle)
      // Speed: 60s for inner, 80s for outer

      const innerAngle = -((elapsed / 60000) * 360) % 360;
      const outerAngle = ((elapsed / 80000) * 360) % 360;

      if (innerRingRef.current) {
        innerRingRef.current.style.setProperty('--rotation', `${innerAngle}deg`);
      }
      if (outerRingRef.current) {
        outerRingRef.current.style.setProperty('--rotation', `${outerAngle}deg`);
      }

      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);
    return () => {
      // Clean up animation frame and reset start time to prevent stale values on remount
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      startTimeRef.current = null;
    };
  }, []);

  return (
    <div 
      className={`relative flex items-center justify-center w-full overflow-visible ${className}`}
    >
      <div 
        className="relative shrink-0 origin-center flex items-center justify-center transition-transform duration-300"
        style={{ 
          width: '560px',
          height: '560px',
          transform: `scale(${scale})`,
          // Collapse document flow footprint exactly to the scaled size! The height will scale correctly.
          margin: `${(560 * (scale - 1)) / 2}px`
        }}
      >
        
        {/* Center Avatar (Geometric Center) */}
        <div className="relative z-10 w-32 h-32 bg-white rounded-full shadow-xl shadow-slate-200/60 flex items-center justify-center border border-white/80">
          {candidateName && candidateName.trim().length > 0 ? (
            <span className="text-5xl font-medium text-slate-800">
              {candidateName.trim().charAt(0).toUpperCase()}
            </span>
          ) : (
            <User className="w-16 h-16 text-slate-400" />
          )}
          {/* Soft glow behind avatar */}
          <div className="absolute inset-0 bg-white/40 blur-2xl -z-10 rounded-full transform scale-150" />
        </div>

        {/* Inner Ring */}
        <div
          ref={innerRingRef}
          className="absolute inset-0 m-auto w-[280px] h-[280px] rounded-full border border-slate-200/60"
          style={{ transform: 'rotate(var(--rotation))', '--rotation': '0deg' }}
        >
          {innerIcons.map((item, i) => {
            const Icon = item.icon || Briefcase;
            const angle = (360 / innerIcons.length) * i;
            
            return (
              <div 
                key={item.id} 
                className="absolute inset-0"
                style={{ transform: `rotate(${angle}deg)` }}
              >
                {/* Position at top edge (radius distance), then counter-rotate */}
                <div 
                  className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{ transform: `translate(-50%, -50%) rotate(calc(var(--rotation) * -1 - ${angle}deg))` }}
                >
                  <div className={`flex items-center justify-center gap-2 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm border border-slate-100/50 whitespace-nowrap transition-transform hover:scale-110 cursor-default`}>
                    <div className={`p-1 rounded-full ${item.bg}`}>
                      <Icon className={`w-4 h-4 ${item.color}`} />
                    </div>
                    <span className="text-xs font-medium text-slate-600">{item.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Outer Ring */}
        <div 
          ref={outerRingRef}
          className="absolute inset-0 m-auto w-[560px] h-[560px] rounded-full border border-slate-200/60"
          style={{ transform: 'rotate(var(--rotation))', '--rotation': '0deg' }}
        >
          {outerIcons.map((item, i) => {
            const Icon = item.icon || Briefcase;
            const angle = (360 / outerIcons.length) * i;

            return (
              <div 
                key={item.id} 
                className="absolute inset-0"
                style={{ transform: `rotate(${angle}deg)` }}
              >
                <div 
                  className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{ transform: `translate(-50%, -50%) rotate(calc(var(--rotation) * -1 - ${angle}deg))` }}
                >
                  <div className={`flex items-center justify-center gap-2 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm border border-slate-100/50 whitespace-nowrap transition-transform hover:scale-110 cursor-default`}>
                    <div className={`p-1.5 rounded-full ${item.bg}`}>
                      <Icon className={`w-5 h-5 ${item.color}`} />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}